import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { AlarmClock, BellOff } from "lucide-mui";
import { useState } from "react";
import DropdownMenu from "../DropdownMenu.tsx";
import FormattedDate from "../FormattedDate.tsx";
import { PRESETS, inDays, isSnoozed, morningOf, tomorrowInput } from "./snooze.ts";

/** "Until [date] Set": a snooze to a day of choice. Not a menu item: typing stays in the field. */
function SnoozeDateRow({ onPick }: { onPick: (until: string) => void }) {
  const [custom, setCustom] = useState("");
  return (
    <Stack direction="row" spacing={1} sx={{ px: 1.75, py: 1, alignItems: "center" }}>
      <TextField
        type="date"
        size="small"
        label="Until"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: tomorrowInput() } }}
      />
      <Button
        size="small"
        // Typed dates can skip the picker's `min`.
        disabled={!custom || custom < tomorrowInput()}
        onClick={() => onPick(morningOf(custom))}
      >
        Set
      </Button>
    </Stack>
  );
}

/**
 * The presets, a date and (when snoozed) "Take off snooze", as a flat list so
 * the menu sees its items. `pick` closes the menu and snoozes.
 */
function snoozeMenuItems(
  snoozedUntil: string | null | undefined,
  pick: (until: string | null) => void,
) {
  const items = PRESETS.map((p) => (
    <MenuItem key={p.days} onClick={() => pick(inDays(p.days))}>
      <ListItemText primary={p.label} />
    </MenuItem>
  ));
  items.push(<Divider key="date-divider" />, <SnoozeDateRow key="date" onPick={pick} />);
  if (isSnoozed(snoozedUntil)) {
    items.push(
      <Divider key="off-divider" />,
      <MenuItem key="off" onClick={() => pick(null)}>
        <ListItemIcon>
          <BellOff fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Take off snooze" />
      </MenuItem>,
    );
  }
  return items;
}

/**
 * "Snooze ▾": put a selection aside until a date. Shows the current date when
 * one row is snoozed, and offers to take it off.
 */
export default function SnoozeMenu({
  snoozedUntil,
  onSnooze,
  disabled,
}: {
  // The one row's current snooze; omit for a selection.
  snoozedUntil?: string | null;
  onSnooze: (until: string | null) => void;
  disabled?: boolean;
}) {
  const snoozed = isSnoozed(snoozedUntil);
  return (
    <DropdownMenu
      label={
        snoozed ? (
          <>
            Snoozed until <FormattedDate value={snoozedUntil!} />
          </>
        ) : (
          "Snooze"
        )
      }
      buttonProps={{
        size: "small",
        variant: snoozed ? "contained" : "outlined",
        color: snoozed ? "primary" : "inherit",
        startIcon: <AlarmClock />,
        disabled,
      }}
    >
      {(close) =>
        snoozeMenuItems(snoozedUntil, (until) => {
          close();
          onSnooze(until);
        })
      }
    </DropdownMenu>
  );
}

/**
 * The snooze presets, a date and "Take off snooze", opened at `anchor`: what
 * "Snooze ▾" shows, for a trigger that lives elsewhere (the drawer's review
 * group, a row's kebab).
 */
export function SnoozeMenuPopover({
  anchor,
  onClose,
  snoozedUntil,
  onSnooze,
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
  snoozedUntil?: string | null;
  onSnooze: (until: string | null) => void;
}) {
  const pick = (until: string | null) => {
    onClose();
    onSnooze(until);
  };
  return (
    <Menu
      anchorEl={anchor}
      open={!!anchor}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { mt: 0.5 } } }}
    >
      {snoozeMenuItems(snoozedUntil, pick)}
    </Menu>
  );
}
